/**
 * AURA Field Annotations — Tufte-style leader lines
 *
 * Projects cluster positions from 3D world space to 2D screen space.
 * Places labels in left/right margins with thin SVG connector lines.
 * Updates every animation frame.
 *
 * Replaces the 3D sprite label system for cluster names.
 * The field is the map. Typography annotates it from the edges.
 */

import * as THREE from 'three';
import type { SyntheticField } from '../data/synthetic';

const MARGIN = 172;            // px from edge to label column
const MIN_CLUSTER_SCALE = 0.3; // only annotate clusters above this intensity
const MAX_LABELS = 12;         // max annotations visible at once
const LINE_COLOR = 'rgba(255,255,255,0.18)';
const DOT_COLOR  = 'rgba(255,255,255,0.35)';
const FADE_ZONE  = 80;         // px — if projected pos is within this of edge, fade

interface Annotation {
  id: string;
  topic: string;
  brief: string;        // 1-line brief from Copy Agent (loaded async)
  screenX: number;
  screenY: number;
  side: 'left' | 'right';
  intensity: number;
  trend: string;
}

export class FieldAnnotations {
  private container: HTMLElement;
  private svg!: SVGSVGElement;
  private labelsEl!: HTMLElement;
  private field: SyntheticField;
  private camera: THREE.Camera;
  private width = 0;
  private height = 0;
  private briefs = new Map<string, string>(); // clusterId → 1-sentence brief
  private visible = true;

  constructor(container: HTMLElement, field: SyntheticField, camera: THREE.Camera) {
    this.container = container;
    this.field = field;
    this.camera = camera;

    this.inject();
    this.loadBriefs();
  }

  private inject() {
    // SVG layer for connector lines
    const svgNS = 'http://www.w3.org/2000/svg';
    this.svg = document.createElementNS(svgNS, 'svg') as SVGSVGElement;
    this.svg.style.cssText = `
      position: absolute; inset: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 8;
    `;
    this.container.appendChild(this.svg);

    // HTML layer for text labels
    this.labelsEl = document.createElement('div');
    this.labelsEl.style.cssText = `
      position: absolute; inset: 0; pointer-events: none; z-index: 9;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    `;
    this.container.appendChild(this.labelsEl);

    // CSS
    const style = document.createElement('style');
    style.textContent = `
      .ann-label {
        position: absolute;
        transition: opacity 0.4s ease;
        width: ${MARGIN - 16}px;
      }
      .ann-label.left  { text-align: right; }
      .ann-label.right { text-align: left; }
      /* Topic: legible, precise, small-caps authority */
      .ann-topic {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.10em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.78);
        line-height: 1.3;
        display: block;
      }
      /* Brief removed — 8.5px at 0.28 opacity is visual noise, not information.
         Topic + scale is sufficient for the annotation role. */
      .ann-brief { display: none; }
      /* Scale: the Tufte dimension callout — precise, tabular */
      .ann-scale {
        font-size: 9px;
        font-weight: 400;
        color: rgba(255,255,255,0.40);
        letter-spacing: 0.06em;
        display: block;
        margin-top: 3px;
        font-variant-numeric: tabular-nums;
      }
      .ann-trend {
        font-size: 9px;
        font-weight: 500;
        margin-top: 4px;
        display: block;
        letter-spacing: 0.04em;
      }
      .ann-trend.rising { color: rgba(79,209,160,0.72); }
      .ann-trend.stable { display: none; } /* stable needs no annotation */
      .ann-trend.fading { color: rgba(209,79,79,0.62); }
    `;
    document.head.appendChild(style);
  }

  private async loadBriefs() {
    const GATEWAY = (import.meta as any).env?.VITE_API_URL || '';
    if (!GATEWAY) return;

    // Get cluster data from API
    const res = await fetch(`${GATEWAY}/api/clusters?limit=20`).catch(() => null);
    if (!res?.ok) return;
    const { clusters } = await res.json() as any;

    // For each cluster, try to get a cached brief from the field nodes
    // (Don't hammer the Copy Agent — use what we have)
    for (const cl of clusters || []) {
      this.briefs.set(cl.id, cl.description?.slice(0, 80) || '');
    }
  }

  setField(field: SyntheticField) {
    this.field = field;
    this.loadBriefs();
  }

  setVisible(v: boolean) {
    this.visible = v;
    this.svg.style.opacity = v ? '1' : '0';
    this.labelsEl.style.opacity = v ? '1' : '0';
  }

  update(canvasWidth: number, canvasHeight: number) {
    // Hide when Sense feed is open — display:none not opacity:0
    // opacity:0 still occupies layout space and can flicker back on transitions
    const senseFeed = document.getElementById('sense-feed');
    const senseVisible = senseFeed?.classList.contains('visible') ?? false;
    if (!this.visible || senseVisible) {
      this.svg.style.display = 'none';
      this.labelsEl.style.display = 'none';
      return;
    }
    this.svg.style.display = '';
    this.labelsEl.style.display = '';
    this.width = canvasWidth;
    this.height = canvasHeight;

    this.svg.setAttribute('viewBox', `0 0 ${canvasWidth} ${canvasHeight}`);

    // Project cluster nodes to screen space
    const annotations: Annotation[] = [];
    const clusterNodes = this.field.nodes.filter(n =>
      n.id.startsWith('cluster-') && !n.id.match(/-e\d+$/) && n.intensity >= MIN_CLUSTER_SCALE
    );

    for (const node of clusterNodes) {
      const worldPos = new THREE.Vector3(...node.position);
      worldPos.project(this.camera);

      // Convert NDC to screen pixels
      const sx = (worldPos.x + 1) / 2 * canvasWidth;
      const sy = (-worldPos.y + 1) / 2 * canvasHeight;

      // Skip if behind camera or off screen vertically
      if (worldPos.z > 1) continue;
      if (sy < 40 || sy > canvasHeight - 60) continue;

      const side: 'left' | 'right' = sx < canvasWidth / 2 ? 'left' : 'right';
      const trend: string = node.trend || 'stable';

      annotations.push({
        id: node.id,
        topic: node.name,
        brief: this.briefs.get(node.id) || '',
        screenX: sx,
        screenY: sy,
        side,
        intensity: node.intensity,
        trend,
      });
    }

    // Sort by intensity descending, cap at MAX_LABELS
    annotations.sort((a, b) => b.intensity - a.intensity);
    const visible = annotations.slice(0, MAX_LABELS);

    this.renderAnnotations(visible);
  }

  private renderAnnotations(annotations: Annotation[]) {
    const svgNS = 'http://www.w3.org/2000/svg';
    this.svg.innerHTML = '';
    this.labelsEl.innerHTML = '';

    // Separate left/right and sort by Y to stack without overlap
    const left  = annotations.filter(a => a.side === 'left').sort((a, b) => a.screenY - b.screenY);
    const right = annotations.filter(a => a.side === 'right').sort((a, b) => a.screenY - b.screenY);

    const stacked = [
      ...this.stackPositions(left,  'left'),
      ...this.stackPositions(right, 'right'),
    ];

    for (const { ann, labelY } of stacked) {
      const opacity = this.edgeFade(ann.screenX, ann.screenY);
      if (opacity < 0.05) continue;

      // Determine label X position
      const labelX = ann.side === 'left'
        ? 8
        : this.width - MARGIN + 8;

      // Endpoint on the label (right edge for left labels, left edge for right labels)
      const lineEndX = ann.side === 'left'
        ? MARGIN - 8
        : this.width - MARGIN + 8;
      const lineEndY = labelY + 6; // align with topic text baseline

      // SVG: dot at cluster position
      const dot = document.createElementNS(svgNS, 'circle');
      dot.setAttribute('cx', String(ann.screenX));
      dot.setAttribute('cy', String(ann.screenY));
      dot.setAttribute('r', '1.5');
      dot.setAttribute('fill', DOT_COLOR);
      dot.setAttribute('opacity', String(opacity));
      this.svg.appendChild(dot);

      // SVG: connector line — horizontal from cluster to margin, then vertical to label
      const midX = ann.side === 'left' ? MARGIN - 8 : this.width - MARGIN + 8;
      const path = document.createElementNS(svgNS, 'path');
      const d = `M ${ann.screenX} ${ann.screenY} H ${midX} V ${lineEndY}`;
      path.setAttribute('d', d);
      path.setAttribute('stroke', LINE_COLOR);
      path.setAttribute('stroke-width', '0.5');
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', String(opacity * 0.9));
      this.svg.appendChild(path);

      // HTML: label
      const label = document.createElement('div');
      label.className = `ann-label ${ann.side}`;
      label.style.cssText = `
        left: ${labelX}px;
        top: ${labelY - 4}px;
        opacity: ${opacity};
      `;

      const trendLabel = ann.trend === 'rising' ? '↑ Rising' : ann.trend === 'fading' ? '↓ Fading' : '';

      // Get signal count + confidence from cluster data
      const clusterNode = this.field.nodes.find(n => n.id === ann.id);
      const confPct = clusterNode ? Math.round(clusterNode.intensity * 100) : 0;
      // Count satellite nodes for signal proxy
      const satCount = this.field.nodes.filter(n => n.id.startsWith(ann.id + '-e')).length;

      label.innerHTML = `
        <span class="ann-topic">${this.esc(ann.topic)}</span>
        <span class="ann-scale">${confPct}% · ${satCount > 0 ? satCount * 6 + '+' : '—'} sig</span>
        ${ann.brief ? `<span class="ann-brief">${this.esc(ann.brief.slice(0, 70))}</span>` : ''}
        ${trendLabel ? `<span class="ann-trend ${ann.trend}">${trendLabel}</span>` : ''}
      `;
      this.labelsEl.appendChild(label);
    }
  }

  /** Stack label Y positions to prevent overlap (min 52px apart) */
  private stackPositions(
    anns: Annotation[],
    side: 'left' | 'right'
  ): Array<{ ann: Annotation; labelY: number }> {
    const result: Array<{ ann: Annotation; labelY: number }> = [];
    const MIN_GAP = 54;
    let lastY = -Infinity;

    for (const ann of anns) {
      let y = ann.screenY;
      if (y < lastY + MIN_GAP) y = lastY + MIN_GAP;
      if (y > this.height - 60) continue;
      result.push({ ann, labelY: y });
      lastY = y;
    }
    return result;
  }

  /** Fade out labels whose cluster is near the screen edges */
  private edgeFade(sx: number, sy: number): number {
    const ex = Math.min(sx, this.width - sx);
    const ey = Math.min(sy, this.height - sy);
    const edge = Math.min(ex, ey);
    if (edge < 0) return 0;
    if (edge < FADE_ZONE) return edge / FADE_ZONE;
    return 1;
  }

  private esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  dispose() {
    this.svg.remove();
    this.labelsEl.remove();
  }
}
